import type { CalculatorContent, Faq } from '@/types/calculator'
import { transferDutyDefaults } from '@/data/defaults'
import type { TransferCostInputs } from './types'

const firstThreshold = transferDutyDefaults.brackets[0].max ?? 0
const lowestDutyRate = Math.min(
  ...transferDutyDefaults.brackets.map((b) => b.rate).filter((r) => r > 0)
)
const topDutyRate = transferDutyDefaults.brackets[transferDutyDefaults.brackets.length - 1].rate

export const content: CalculatorContent<TransferCostInputs> = {
  intro:
    'Budget for the full cost of buying property with this transfer duty calculator for South Africa. It estimates transfer duty, conveyancing fees, deeds office charges and bond registration costs — the cash you need on top of the purchase price.',
  howItWorks: [
    `Transfer duty follows the SARS sliding-scale brackets: no duty up to R${firstThreshold.toLocaleString('en-ZA')}, then marginal rates from ${lowestDutyRate * 100}% up to ${topDutyRate * 100}% on higher-priced properties. The same rates apply to individuals, companies and trusts.`,
    'Conveyancing and bond registration fees are simplified estimates tiered by property value and loan amount — actual attorney fees are quoted per transaction. Small deeds office, search/FICA and postage charges are added as fixed estimates.',
    'The total shown is the estimated cash required for the transfer itself, excluding the deposit, moving costs and ongoing municipal rates.',
  ],
  formulas: [
    {
      label: 'Transfer duty',
      expression: 'bracket base + bracket rate × (price − previous bracket ceiling)',
      notes: `First R${firstThreshold.toLocaleString('en-ZA')} is duty-free; top marginal rate ${topDutyRate * 100}%.`,
    },
    {
      label: 'Total cash required',
      expression:
        'transfer duty + conveyancing + deeds office + bond registration + search + postage',
    },
  ],
  example: {
    title: 'Buying a R1.5 million home with a bond',
    description:
      'A buyer purchases a home financed with a bond and wants to know the bond and transfer costs payable before registration.',
    inputs: {
      purchasePrice: 1_500_000,
      financed: true,
      loanAmount: 1_350_000,
    },
  },
  definitions: [
    {
      term: 'Transfer duty',
      description:
        'A tax paid to SARS when property changes ownership, calculated on a sliding scale of the purchase price.',
    },
    {
      term: 'Conveyancing fee',
      description:
        'The transferring attorney’s professional fee for registering the property in the buyer’s name.',
    },
    {
      term: 'Bond registration',
      description:
        'The separate attorney process (and fee) for registering the buyer’s bond at the deeds office.',
    },
    {
      term: 'Deeds office fee',
      description: 'The government registry charge for recording the transfer and bond.',
    },
    {
      term: 'Postage and petties',
      description:
        'Small administrative disbursements — couriers, copies and sundries — charged by the attorneys.',
    },
  ],
}

export const faqs: Faq[] = [
  {
    question: 'Are these fees exact?',
    answer:
      'No. Conveyancing and bond registration fees vary by attorney and property complexity. Use these figures for budgeting and request a formal quote.',
  },
  {
    question: 'When is no transfer duty payable?',
    answer: `No transfer duty is payable on properties up to R${firstThreshold.toLocaleString('en-ZA')} under the current SARS brackets, whoever the buyer is.`,
  },
  {
    question: 'Do companies pay different transfer duty?',
    answer:
      'No. Since 23 February 2011 SARS applies the same sliding-scale rates to companies, close corporations and trusts as to individuals.',
  },
  {
    question: 'Is transfer duty payable if the seller is VAT-registered?',
    answer:
      'Usually not — if the seller is a VAT vendor selling the property as part of their enterprise, VAT applies to the sale instead of transfer duty. This calculator assumes a transfer-duty transaction.',
  },
  {
    question: 'Are bond and transfer costs included in my home loan?',
    answer:
      'Usually not — they are paid in cash before registration, which is why the calculator shows a separate total cash required figure.',
  },
]
